/* eslint react/forbid-prop-types: off */
import _ from 'lodash'
import omit from 'lodash/omit'
import PropTypes from 'prop-types'
import { forwardRef, isValidElement, useCallback, useRef } from 'react'
import Select, { components } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { forceArray } from '~/common/arrayUtils'
import { asFormikField } from '~/common/formikUtils'
import { isClient } from '~/common/nextJsUtils'
import { isOptionShape } from '~/common/objectUtils'
import { TOP_NAV_HEIGHT } from '~/common/scrollUtils'
import { Box, useFormControlContext } from '~/design_system'
import { Msg, useMsg } from '~/common/localizationUtils'
import { useFormControlExtendedContext } from '~/design_system/components/form_controls/FormControlExtendedContext'
import { mergeRefs } from '~/common/reactUtils'

export const vndlySelectTheme = theme => ({
  ...theme,
  borderRadius: 2,
  colors: {
    ...theme.colors,
    primary: '#26b999',
    primary25: '#F5F5F5',
    neutral80: '#59636F'
  }
})

const isNonValue = val => val === null || val === undefined || val === ''

const getInactiveStyledLabel = (data, isSelected) =>
  // Selected option or singleValue that have an $inactive property set to `true`
  // will look grayed out and have extra labeling e.g. 'Some Option (Inactive)'
  // which can be customized using $inactive_label, e.g. 'Some Option (Deleted)'
  data?.$inactive && {
    ...(!isSelected && { color: '#a0aab6' }),
    '& div:after': { content: `' ${data?.$inactive_label ?? '(Inactive)'}'` }
  }

export const vndlySelectStyles = containerStyles => ({
  option: (provided, { data, type, value, isSelected }) => ({
    ...provided,
    ...(type === 'option' && isNonValue(value) && !isSelected && { color: '#a0aab6' }),
    ...(type === 'option' && getInactiveStyledLabel(data, isSelected)),
    fontWeight: '400',
    fontSize: '14px',
    minHeight: '2.375rem',
    ...containerStyles
  }),
  singleValue: (provided, { data, selectProps: { readOnly } }) => ({
    ...provided,
    ...(isNonValue(data?.value) && { color: '#a0aab6' }),
    ...getInactiveStyledLabel(data),
    fontWeight: 'var(--vndly-font-weight-normal)',
    fontSize: 'var(--vndly-font-size-md)',
    ...(readOnly && { color: 'var(--vndly-color-container-dark)', margin: 0 })
  }),
  container: provided => ({
    ...provided,
    ...containerStyles
  }),
  control: (provided, { isFocused, selectProps: { readOnly } }) => ({
    ...provided,
    ...(readOnly && { background: 'transparent', border: 'none' }),
    ...containerStyles.control,
    '.has-error &, [data-invalid] ~ [data-qa="select"] &, [data-react-select][data-invalid="true"] &': {
      borderColor: '#c2160a',
      ...(isFocused && { boxShadow: '0 0 0 1px rgba(194,22,10,0.5)' })
    },
    '[data-warned] ~ [data-qa="select"] &, [data-react-select][data-warned="true"] &': {
      borderColor: '#ffa126',
      ...(isFocused && { boxShadow: '0 0 0 1px rgba(194,22,10,0.5)' })
    }
  }),
  valueContainer: (provided, { selectProps: { readOnly, isMulti } }) => ({
    ...provided,
    ...(readOnly && { padding: '2px 0' }),
    ...(isMulti && { padding: '0 8px' }),
    minHeight: '2.375rem'
  }),
  menu: provided => ({
    ...provided,
    zIndex: 10
  }),
  menuPortal: provided => ({
    ...provided,
    zIndex: 1500
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (provided, { selectProps: { readOnly } }) => ({
    ...provided,
    ...(readOnly && { display: 'none' }),
    padding: '0 6px',
    transform: 'scale(.75)'
  }),
  clearIndicator: provided => ({
    ...provided,
    padding: '8px 3px',
    position: 'relative',
    left: 5
  }),
  placeholder: (provided, { selectProps: { readOnly, value } }) => ({
    ...provided,
    ...(readOnly && (!value || value.length === 0) && { display: 'none' })
  }),
  multiValue: (provided, { selectProps: { isMulti } }) => ({
    ...provided,
    ...(isMulti && { borderRadius: 4 })
  }),
  multiValueLabel: (provided, { data, selectProps: { readOnly, isMulti } }) => ({
    ...provided,
    ...(isMulti && { fontSize: '100%', padding: '6px 2px 6px 8px' }),
    ...((readOnly || data?.$locked) && { paddingRight: '6px' })
  }),
  multiValueRemove: (provided, { data, selectProps: { readOnly } }) => {
    return {
      ...provided,
      ...(readOnly && { display: 'none' }),
      ...(data?.$locked && { display: 'none' })
    }
  }
})

const customStyle = (vndlyStyles, customStyles) => (provided, state) => ({
  ...provided,
  ...(typeof vndlyStyles === 'function' ? vndlyStyles(provided, state) : vndlyStyles),
  ...(typeof customStyles === 'function' ? customStyles(provided, state) : customStyles)
})

// Merge styles
export const mergeSelectStyles = (stylesOverrides, vndlyStyles) =>
  Object.keys(stylesOverrides).reduce(
    (customStyles, key) => ({ ...customStyles, [key]: customStyle(customStyles[key], stylesOverrides[key]) }),
    vndlySelectStyles(vndlyStyles)
  )

export const buildVndlySelectStyles = props => {
  const { style, styles } = props
  return _.isEmpty(style) ? mergeSelectStyles(styles, vndlySelectStyles({})) : vndlySelectStyles(style)
}

/**
 * Extend react-select's SelectContainer to add title to the div in order to render a tooltip
 */
const SelectContainer = ({ getValue, innerProps, ...props }) => {
  let value = getValue()
  // Even for non-multi selects, we get an array here.
  if (Array.isArray(value) && value.length > 0) {
    value = value[0]
  }
  const title = isOptionShape(value) ? value.label : value
  return <components.SelectContainer {...props} innerProps={{ ...innerProps, title }} />
}

const MultiValueLabel = ({ innerProps, data, ...props }) => {
  const title = data.label
  return (
    <components.MultiValueLabel {...props} data={data} innerProps={{ ...innerProps, title }}>
      <CustomLabel {...data} />
    </components.MultiValueLabel>
  )
}

// Support to wrap label in a custom component
const CustomLabel = ({ label = {}, subLabel = '', LabelContainer, isSelected }) => {
  const subLabelStyle = {
    fontSize: '13px',
    color: isSelected ? '#F5F5F5' : '#6fa196'
  }

  const labelDisplay = (
    <>
      {/* For some reason in the React 18 upgrade label can sometimes be {} */}
      <Box className="vndly-select-option-label" whiteSpace="normal">
        {Object.keys(label).length === 0 ? '' : label}
      </Box>
      {subLabel && (
        <div className="vndly-select-option-sublabel" style={subLabelStyle}>
          {subLabel}
        </div>
      )}
    </>
  )

  if (LabelContainer) {
    return <LabelContainer>{labelDisplay}</LabelContainer>
  }
  return <>{labelDisplay}</>
}

// Add support to render icons along with the selected Option
const SingleValue = ({ data, ...props }) => (
  <components.SingleValue data={data} {...props}>
    <CustomLabel {...data} />
  </components.SingleValue>
)

// Add support to render icons along with Options
const Option = ({ data, ...props }) => {
  return (
    <components.Option data={data} {...props}>
      {/* eslint-disable-next-line react/destructuring-assignment */}
      <CustomLabel {...data} isSelected={props.isSelected} />
    </components.Option>
  )
}

const MenuList = ({ children, ...props }) => (
  <div data-react-select-menu-list>
    {/* eslint-disable-next-line react/destructuring-assignment */}
    {props.selectProps.menuListMessage ? (
      <components.MenuList {...omit(props, ['id'])} innerProps={omit(props.innerProps, ['id'])}>
        {props.selectProps.menuListMessage}
      </components.MenuList>
    ) : null}
    <components.MenuList {...props}>{children}</components.MenuList>
  </div>
)

const Input = props => {
  // aria-label and aria-labelledby are already spread by the existing props if provided
  const ariaProps = {
    'aria-describedby': [props['aria-describedby'], props.selectProps['aria-describedby']].join(' ')
  }

  return <components.Input {...props} {...ariaProps} />
}

const singleComponents = {
  SelectContainer,
  SingleValue,
  Option,
  MenuList,
  Input
}
const multiComponents = {
  MultiValueLabel,
  Option,
  Input
}

export const mapSimpleStringOptions = (options, emptyLabel, msg = null) => {
  return Array.isArray(options)
    ? options.map(option => {
        const shaped = isOptionShape(option) ? { ...option } : { label: option, value: option }
        if (!shaped.label && !shaped.value) shaped.label = emptyLabel
        // Handle calling `msg()` on the label if it was created with <Msg /> or lazyMsg()
        if (msg && isValidElement(shaped.label) && shaped.label.type === Msg) {
          shaped.label = msg(shaped.label)
        }
        return shaped
      })
    : options
}

function optionizeValue(val, { creatable } = {}) {
  if (isOptionShape(val)) {
    if (creatable && val.label === '') return { label: val.value, value: val.value }
    return val
  }
  return { label: val, value: val }
}

export function getOptionsThatMatchValue(value, options, { creatable } = {}) {
  // eslint-disable-next-line no-prototype-builtins
  const optionsAreInGroups = options[0]?.hasOwnProperty('label') && options[0]?.hasOwnProperty('options')
  const newOptions = optionsAreInGroups ? options.flatMap(group => group.options) : options
  // Loop through the selected value(s)
  return forceArray(value).map(val => {
    // If we can find a match in the options, map to it
    const matchingOption = newOptions.find(option => option?.value === (val?.value ?? val))
    if (matchingOption != null) return matchingOption

    // Otherwise "optionize" the value if necessary
    return optionizeValue(val, { creatable })
  })
}

export const VndlySelectInternal = forwardRef(function VndlySelectInternal(
  {
    creatable,
    disabled,
    options = [],
    value = null,
    isMulti = false,
    readOnly,
    id: idProp = null,
    emptyLabel = 'No Selection',
    onChange = () => {},
    onMouseEnter = _.noop,
    menuPortal = true,
    placeholder,
    name = 'select',
    onBlur = () => {},
    isSearchable = true,
    style = {},
    styles = {},
    testId = 'select',
    ...props
  },
  outerRef
) {
  const innerRef = useRef(null)
  const ref = mergeRefs(outerRef, innerRef)
  const otherProps = {
    name,
    onBlur,
    isSearchable,
    style,
    styles,
    testId,
    ...props
  }
  const msg = useMsg()
  const selectOptions = mapSimpleStringOptions(options, emptyLabel, msg)
  let selectValue = value
  if (value !== null) {
    const matchingOpts = getOptionsThatMatchValue(value, selectOptions, { creatable })
    selectValue = isMulti ? matchingOpts : matchingOpts[0]
  }
  const getOptionLabelSelect = option => `${option.label}${option.subLabel ? ` ${option.subLabel}` : ''}`
  const internalOnChange = (values, actionObj) => {
    let optionValues = values
    switch (actionObj.action) {
      case 'remove-value':
      case 'pop-value':
        if (actionObj.removedValue?.$locked) {
          return
        }
        break
      case 'clear':
        optionValues = options.filter(v => v.$locked)
        break
      default:
        break
    }
    onChange(optionValues, actionObj)
  }

  const {
    isDisabled,
    isReadOnly,
    isInvalid,
    id: formControlId,
    hasHelpText,
    helpTextId
  } = useFormControlContext() ?? {}

  const { isWarned } = useFormControlExtendedContext() ?? {}

  const id = idProp ?? formControlId
  const closeMenuProps = useCloseMenuIfItGoesBehindAnything(innerRef, otherProps)
  const localeNoOptionsMessage = () => {
    return msg('common.ui.no_options')
  }
  const selectProps = {
    placeholder: placeholder === undefined ? msg('common.ui.select_placeholder') : placeholder,
    noOptionsMessage: localeNoOptionsMessage,
    menuPlacement: 'auto',
    menuPosition: 'fixed',
    isMulti,
    ...(isInvalid && { 'aria-invalid': isInvalid }),
    'aria-errormessage': `${id}-feedback`,
    'aria-describedby': hasHelpText ? helpTextId : '',
    ...(menuPortal && isClient() && { menuPortalTarget: document.body }),
    ...otherProps,
    options: selectOptions,
    value: selectValue,
    theme: vndlySelectTheme,
    styles: buildVndlySelectStyles(otherProps),
    isDisabled: disabled || readOnly || isDisabled || isReadOnly,
    readOnly: readOnly || isReadOnly,
    inputId: id,
    instanceId: id,
    components: isMulti ? multiComponents : singleComponents,
    getOptionLabel: getOptionLabelSelect,
    onChange: internalOnChange,
    isClearable:
      isMulti && Array.isArray(value)
        ? value?.some(
            v =>
              !_.get(
                options?.find(opt => opt?.value === v?.value),
                '$locked',
                false
              )
          )
        : _.get(otherProps, 'isClearable', false),
    ...closeMenuProps
  }

  return (
    <div
      data-react-select
      data-qa={selectProps.testId}
      data-testid={selectProps.testId}
      onMouseEnter={onMouseEnter}
      data-warned={isWarned}
      data-invalid={isInvalid}>
      {creatable ? <CreatableSelect ref={ref} {...selectProps} /> : <Select ref={ref} {...selectProps} />}
    </div>
  )
})

VndlySelectInternal.displayName = 'VndlySelectInternal'

function useCloseMenuIfItGoesBehindAnything(ref, props) {
  const isMenuOpenRef = useRef(false)
  const isMenuClippedByControlScrollParent = control => {
    let el = control.parentElement
    const menuRect = control.getBoundingClientRect()
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el)
      const overflowY = style.overflowY
      const overflowX = style.overflowX
      const hasScroll = /(auto|scroll|hidden)/.test(overflowY + overflowX)
      if (hasScroll) {
        const containerRect = el.getBoundingClientRect()
        const clipped =
          menuRect.bottom > containerRect.bottom ||
          menuRect.top < containerRect.top ||
          menuRect.left < containerRect.left ||
          menuRect.right > containerRect.right
        if (clipped) return true
      }
      el = el.parentElement
    }
    return false
  }
  return {
    closeMenuOnScroll: () => {
      const select = ref.current.inputRef
      const menuList = ref.current.menuListRef
      const control = ref.current.controlRef
      if (props.closeMenuOnScroll && !select && !menuList) {
        return true
      }
      if (!isMenuOpenRef.current) {
        return false
      }
      if (!menuList) {
        return false
      }
      const top = menuList.getBoundingClientRect().top
      if (top < TOP_NAV_HEIGHT) {
        return true
      }
      return isMenuClippedByControlScrollParent(control)
    },
    onMenuOpen: useCallback(
      (...args) => {
        isMenuOpenRef.current = true
        return props.onMenuOpen?.(...args)
      },
      [props]
    ),
    onMenuClose: useCallback(
      (...args) => {
        isMenuOpenRef.current = false
        return props.onMenuClose?.(...args)
      },
      [props]
    )
  }
}
const optionShape = PropTypes.oneOfType([
  PropTypes.any,
  PropTypes.shape({
    value: PropTypes.any,
    label: PropTypes.string
  })
])
/*
  TODO: add `autoComplete` prop when it is configurable [PR: https://github.com/JedWatson/react-select/pull/4105]
  This prop is required to properly disable chrome's autofill being shown in Chrome ver. 86+ and few possible solutions(?) are listed in thread
  https://bugs.chromium.org/p/chromium/issues/detail?id=914451&can=2&q=Autofill%20&colspec=ID%20Pri%20M%20Stars%20ReleaseBlock%20Component%20Status%20Owner%20Summary%20OS%20Modified
*/
VndlySelectInternal.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([optionShape, PropTypes.arrayOf(optionShape)]),
  options: PropTypes.arrayOf(optionShape),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  isSearchable: PropTypes.bool,
  isMulti: PropTypes.bool,
  style: PropTypes.object,
  styles: PropTypes.object,
  testId: PropTypes.string
}

// When used in a Formik Form, VndlySelect behaves like a normal select. It only cares
// about the 'value' of the option, not the whole option object. This goes for both
// value and onChange.
export const formikPropMapper = ({
  field,
  form,
  options: _options,
  isMulti,
  extendOnChange = () => {},
  onCreate = null,
  ...rest
}) => ({
  onChange: (option, { action }) => {
    extendOnChange(option, { action })
    if (option === null) {
      form.setFieldValue(field.name, isMulti ? [] : null)
      return
    }
    switch (action) {
      case 'select-option':
      case 'remove-value':
        form.setFieldValue(field.name, isMulti ? option.map(o => o.value) : option.value)
        break
      case 'pop-value':
        break
      case 'clear':
        form.setFieldValue(field.name, null)
        break
      case 'create-option':
        if (typeof onCreate === 'function') {
          onCreate(isMulti ? option.map(o => o.value) : option.value)
        } else {
          form.setFieldValue(field.name, isMulti ? option.map(o => o.value) : option.value)
        }
        break
      default:
        break
    }
  },
  ...rest
})

const VndlySelect = asFormikField(VndlySelectInternal, formikPropMapper)
VndlySelect.displayName = 'VndlySelect'
export default VndlySelect
